import { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { findNodeByCodes } from '@/utils/fsCodes';
import { buildExcludedBankSummaryKeys, buildExcludedBankSummaryKeysFromDataKeys, isBankCashColumn, isExRateColumn, isRateGlCode } from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { isPpeColumn } from '@/components/account/gl-invoice/mode/tabs/ppeColumnUtils';

export interface FsLine {
    fsId: number;
    amount: number;
    lastYearChangeAudited?: number;
    /** MULTI FS：PL 行挂 YOA；缺省时 Audit 周期 Tab 会把 Client 切成 0 */
    yearOfAssessment?: string | null;
}

export interface MissingFsCodeEntry {
    fsCode: string;
    currentAmount: number;
    priorAmount: number;
}

export interface GlToFsMappingResult {
    bsLines: FsLine[];
    plLines: FsLine[];
    missingFsCodes: MissingFsCodeEntry[];
    glCurrentByFsCode: Record<string, number>;
    glPriorByFsCode: Record<string, number>;
}

export interface GlToFsDisplayValues {
    bsValues: Record<number, string>;
    plValues: Record<number, string>;
    bsLastYearValues: Record<number, string>;
    plLastYearValues: Record<number, string>;
    /**
     * B6.1 BA73：−SUM(PL 第10行)，与 AL 上年拆分同源。
     * FS Summary 注入后应写入 plAfterTaxLastYear，避免 PL 树 COS/PUR 模式与拆分不一致导致 Cap≠Net Assets。
     */
    lfpLastYear: number;
    /**
     * 兼容旧字段：B6.1 已改为拆分 AL（见 applyB61AlLastYearSplit），不再压制 LFP 上年。
     * 恒为 false。
     */
    suppressPlAfterTaxLastYear: boolean;
}

/**
 * 按 fsCode 聚合 GL 汇总值（跳过 Ex-rate 列）。
 * 以 summary 的 colKey（fsCode|glCode）为数据源，避免 legacy 导入列不在 columnDefs 时被漏计
 * （例如 BS-NCA-PPE 下 A-FA-ADEQP / Payment in advance）。
 */
export function buildGlTotalByFsCode(
    columnDefs: GlColumnDef[],
    summary: Record<string, number>,
): Record<string, number> {
    const excludedBankKeys = new Set<string>([
        ...(columnDefs.length > 0 ? buildExcludedBankSummaryKeys(columnDefs) : []),
        ...buildExcludedBankSummaryKeysFromDataKeys(Object.keys(summary || {})),
    ]);
    const result: Record<string, number> = {};
    for (const [key, raw] of Object.entries(summary)) {
        const pipeIdx = key.indexOf('|');
        if (pipeIdx <= 0) continue;
        const fsCode = key.slice(0, pipeIdx);
        const glCode = key.slice(pipeIdx + 1);
        if (!fsCode || isRateGlCode(glCode) || excludedBankKeys.has(key)) continue;
        const val = Number(raw) || 0;
        result[fsCode] = (result[fsCode] || 0) + val;
    }
    return result;
}

/** 在 FS 树中按 fsId 查找节点 */
export function findNodeInTree(trees: any[], targetFsId: number): any | null {
    for (const n of trees || []) {
        if (Number(n?.fsId) === targetFsId) return n;
        if (Array.isArray(n?.children) && n.children.length > 0) {
            const found = findNodeInTree(n.children, targetFsId);
            if (found) return found;
        }
    }
    return null;
}

/** 获取节点的第一个叶子后代的 fsId（节点本身是叶子则返回自身 fsId） */
export function getFirstLeafFsId(node: any): number {
    const children = Array.isArray(node?.children) ? node.children : [];
    if (children.length === 0) return Number(node.fsId);
    return getFirstLeafFsId(children[0]);
}

/**
 * 父级 fsCode 节点写入第一个叶子 fsId，与 FS Summary 预览及 Audit FS 叶子行一致。
 */
export function resolveTargetFsId(
    fsCode: string,
    nodeFsId: number,
    bsTree: any[] | undefined | null,
    plTree: any[] | undefined | null,
): number {
    const tree = fsCode.startsWith('PL-') ? (plTree || []) : (bsTree || []);
    const treeNode = findNodeInTree(tree, nodeFsId);
    if (treeNode && Array.isArray(treeNode.children) && treeNode.children.length > 0) {
        return getFirstLeafFsId(treeNode);
    }
    return nodeFsId;
}

/** byCode 未索引时（如 level-2 金额行 PL-TAX）回退到树查找 */
function resolveGlFsNode(
    fsCode: string,
    byCode: Record<string, { fsId?: number } | undefined>,
    bsTree: any[] | undefined | null,
    plTree: any[] | undefined | null,
): { fsId: number } | null {
    const fromMap = byCode[fsCode];
    if (fromMap?.fsId) return { fsId: fromMap.fsId };
    const tree = fsCode.startsWith('PL-') ? (plTree || []) : (bsTree || []);
    const found = findNodeByCodes(tree as any[], [fsCode]);
    const fsId = Number((found as any)?.fsId);
    return fsId ? { fsId } : null;
}

function isNonZero(n: number): boolean {
    return Math.abs(n) > 0.001;
}

const FS_DIR_FROM = 'BS-CA-DIR';
const FS_DIR_TO = 'BS-CL-DTD';

/**
 * GL 董事往来：Excel 单列负数表示「欠董事」→ BS-CL-DTD（Amount due to a director）。
 * 正数留在 BS-CA-DIR。
 */
export function applyDirectorDueFromToSplit(amountsByFsCode: Record<string, number>): void {
    const fromVal = amountsByFsCode[FS_DIR_FROM] ?? 0;
    if (fromVal >= -0.001) return;
    amountsByFsCode[FS_DIR_TO] = (amountsByFsCode[FS_DIR_TO] ?? 0) + fromVal;
    amountsByFsCode[FS_DIR_FROM] = 0;
}

/**
 * Excel Row 5 / GT 收入列：数据行贷方汇总为负，FS 与 Excel 第 5 行一致存正数。
 */
export function normalizeGlPlIncomeAmount(fsCode: string, amount: number): number {
    const code = (fsCode || '').trim().toUpperCase();
    if ((code.startsWith('PL-TURN-') || code.startsWith('PL-OI-')) && amount < -0.001) {
        return Math.abs(amount);
    }
    return amount;
}

/**
 * BS-EQ-AL：对齐 Excel 第 5 行（正=Retained profits，负=Accumulated losses）。
 * Cr 模板 Row5 = -Row6GT；旧模板（无 Row5 公式）直接用 GT 原值。
 */
export function glAlAmountFromExcelRow5(
    rawFromExcelGt: number,
    excelRow5CrFlip = true,
): number {
    if (Math.abs(rawFromExcelGt) < 0.001) return 0;
    if (!excelRow5CrFlip) return rawFromExcelGt;
    return -rawFromExcelGt;
}

/** 从 columnDefs 解析 BS-EQ-AL 是否走 Row5 Cr 翻转（默认 true，与标准模板一致） */
export function resolveAlExcelRow5CrFlip(columnDefs: GlColumnDef[]): boolean {
    const alCol = columnDefs.find((d) => d.fsCode === 'BS-EQ-AL');
    if (alCol?.alExcelRow5CrFlip === false) return false;
    return true;
}

/**
 * B6.1 / B6.2 的 G 列对 GL 第 6 行合计的符号（写死，不读 Excel）。
 * +1 = `+GL!列合计`，-1 = `-GL!列合计`。
 *
 * B6.1：资产 +；流动/非流动负债、权益（IC / OFFS / AL / DIV / LFP）−。
 * B6.2：收入、其他收入 −；成本、行政费用、财务费用、税 +。
 * 存货期初/期末不取第 6 行（B6 引用的是 B6.1 存货格）。
 */
export function b6Row6Sign(fsCode: string): 1 | -1 | 0 {
    const code = (fsCode || '').trim().toUpperCase();
    if (!code || code === 'PL-COT-OI' || code === 'PL-COT-CI') return 0;
    if (
        code.startsWith('BS-CL-')
        || code.startsWith('BS-NCL-')
        || code.startsWith('BS-EQ-')
        || code.startsWith('PL-TURN-')
        || code.startsWith('PL-OI-')
    ) {
        return -1;
    }
    return 1;
}

/** 将 Excel 第 6/10 行原值乘以 B6 符号。 */
export function applyB6Row6Sign(fsCode: string, rowAmount: number): number {
    return b6Row6Sign(fsCode) * rowAmount;
}

/** 由库内「明细合计 + 第10行结转」还原 Excel 第 6 行。 */
export function excelRow6FromSummaries(movement: number, prior: number): number {
    return (movement || 0) + (prior || 0);
}

/**
 * B6.1 BA73：`=-SUM(GL!BV10:FF10)` — LFP Last Year = −(PL 第10行原值合计)。
 * Excel 范围 BV:FF 不含期初/期末存货（PL-COT-OI / PL-COT-CI 在 FI:FJ），
 * 与 b6Row6Sign 对 OI/CI 返回 0 一致；误计入会使 AL 拆分偏差（存货变动额）。
 *
 * PL-COT-COS 与 PL-COT-PUR 不得同时计入（与 PL 树 primary cost 一致：COS 优先）。
 * 两者同额时（无存货 / COS 由 PUR 写入）会把成本加两次，AL 拆分偏小、Cap 比 Net Assets 多出该成本额。
 */
export function computeB61LfpLastYear(glPriorByFsCode: Record<string, number>): number {
    const cosRaw = Number(glPriorByFsCode?.['PL-COT-COS']) || 0;
    const preferCos = isNonZero(cosRaw);
    let rawSum = 0;
    for (const [fsCode, raw] of Object.entries(glPriorByFsCode || {})) {
        const code = (fsCode || '').trim().toUpperCase();
        if (!code.startsWith('PL-')) continue;
        if (code === 'PL-COT-OI' || code === 'PL-COT-CI') continue;
        // COS 有值时跳过 PUR，避免成本双计
        if (preferCos && code === 'PL-COT-PUR') continue;
        rawSum += raw || 0;
    }
    return -rawSum;
}

/**
 * B6.1 BA72：`=-GL!BU10-BA73`
 * GL 第10行 AL 已含上年损益滚存；展示上年 AL 时减去 LFP，避免与 BS-EQ-LFP 双计。
 * `priorAmounts['BS-EQ-AL']` 此时已是 B6 符号后的 −GL!BU10。
 */
export function applyB61AlLastYearSplit(
    priorAmounts: Record<string, number>,
    glPriorByFsCode: Record<string, number>,
): number {
    const lfpLy = computeB61LfpLastYear(glPriorByFsCode);
    if (!isNonZero(lfpLy) && priorAmounts['BS-EQ-AL'] == null) return lfpLy;
    const alFull = priorAmounts['BS-EQ-AL'] ?? 0;
    priorAmounts['BS-EQ-AL'] = alFull - lfpLy;
    return lfpLy;
}

function buildResolvedGlAmountMaps(
    allFsCodes: Set<string>,
    glCurrentByFsCode: Record<string, number>,
    glPriorByFsCode: Record<string, number>,
    _opts?: {
        bsTree?: any[] | null;
        plTree?: any[] | null;
        byCode?: Record<string, { fsId?: number } | undefined>;
        columnDefs?: GlColumnDef[];
    },
): { currentAmounts: Record<string, number>; priorAmounts: Record<string, number> } {
    const currentAmounts: Record<string, number> = {};
    const priorAmounts: Record<string, number> = {};

    // Excel 第 6 行公式因 BS/PL 不同：
    //   BS：SUM(第10行:明细) → 库内明细合计 + 第10行结转
    //   PL：SUM(第11行:明细) → 只要明细合计（第10行是上年比较，不进本期）
    // Current = B6符号 × 第6行；Last Year = B6符号 × 第10行。
    for (const fsCode of allFsCodes) {
        const sign = b6Row6Sign(fsCode);
        if (sign === 0) continue;
        const movement = glCurrentByFsCode[fsCode] ?? 0;
        const prior = glPriorByFsCode[fsCode] ?? 0;
        const row6 = fsCode.startsWith('PL-')
            ? movement
            : excelRow6FromSummaries(movement, prior);
        const amount = sign * row6;
        const lastYear = sign * prior;
        if (!isNonZero(amount) && !isNonZero(lastYear)) continue;
        currentAmounts[fsCode] = amount;
        priorAmounts[fsCode] = lastYear;
    }

    // B6.1：上年 AL = −GL!BU10 − LFP；同步写入 BS-EQ-LFP，与 Cap 合计同源
    const lfpLy = applyB61AlLastYearSplit(priorAmounts, glPriorByFsCode);
    if (isNonZero(lfpLy) || priorAmounts['BS-EQ-AL'] != null) {
        priorAmounts['BS-EQ-LFP'] = lfpLy;
    }

    return { currentAmounts, priorAmounts };
}

/**
 * FS Summary 金额已按 B6 符号写好，展示层不再取绝对值。
 */
export function normalizeGlFsSummaryDisplayAmount(
    _fsCode: string | null | undefined,
    amount: number,
    _parentFsCode?: string | null,
): number {
    return amount;
}

/**
 * Excel legacy GL：AC Row 6/GT = SUM(AC10:AI10)
 * = 上年 Accumulated Profit + 上年 PL-AE 费用列合计 + 当期 AC 列变动
 */
export function computeGlRetainedEarningsRow6(
    glPriorByFsCode: Record<string, number>,
    glCurrentByFsCode: Record<string, number>,
): number {
    const alPrior = glPriorByFsCode['BS-EQ-AL'] ?? 0;
    const alMovement = glCurrentByFsCode['BS-EQ-AL'] ?? 0;
    let priorPlAe = 0;
    for (const [fsCode, val] of Object.entries(glPriorByFsCode)) {
        if (fsCode.startsWith('PL-AE')) priorPlAe += val;
    }
    return alPrior + priorPlAe + alMovement;
}

/** GL Current Period 显示值：Excel 第 6 行 × B6 符号（与 FS Summary / Submit 同源） */
export function resolveGlCurrentPeriodDisplay(
    fsCode: string,
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
    columnDefs: GlColumnDef[] = [],
): number {
    const glCurrent = buildGlTotalByFsCode(columnDefs, summaryCurrentPeriod);
    const glPrior = buildGlTotalByFsCode(columnDefs, summaryPriorYear);
    const sign = b6Row6Sign(fsCode);
    if (sign === 0) return 0;
    const movement = glCurrent[fsCode] ?? 0;
    const prior = glPrior[fsCode] ?? 0;
    const row6 = fsCode.startsWith('PL-')
        ? movement
        : excelRow6FromSummaries(movement, prior);
    return sign * row6;
}

/**
 * Tiny Mode / FS Summary 同源：按 fsCode 取 Current + Last Year 展示额。
 * BS-EQ-AL Last Year 按 B6.1：−GL!BU10 − LFP（见 applyB61AlLastYearSplit）。
 */
export function resolveGlFsCodeDisplayAmounts(
    fsCode: string,
    summaryCurrentPeriod: Record<string, number>,
    summaryPriorYear: Record<string, number>,
    opts: {
        columnDefs?: GlColumnDef[];
        bsTree?: any[] | null;
        plTree?: any[] | null;
        byCode?: Record<string, { fsId?: number } | undefined>;
    } = {},
): { current: number; prior: number } {
    const columnDefs = opts.columnDefs ?? [];
    const glCurrentByFsCode = buildGlTotalByFsCode(columnDefs, summaryCurrentPeriod);
    const glPriorByFsCode = buildGlTotalByFsCode(columnDefs, summaryPriorYear);
    const allFsCodes = new Set([
        ...Object.keys(glCurrentByFsCode),
        ...Object.keys(glPriorByFsCode),
    ]);
    const { currentAmounts, priorAmounts } = buildResolvedGlAmountMaps(
        allFsCodes,
        glCurrentByFsCode,
        glPriorByFsCode,
        {
            bsTree: opts.bsTree,
            plTree: opts.plTree,
            byCode: opts.byCode ?? {},
            columnDefs,
        },
    );
    return {
        current: currentAmounts[fsCode] ?? 0,
        prior: priorAmounts[fsCode] ?? 0,
    };
}

/**
 * 将 GL 当期/上年汇总映射为 changeFs 所需的 BS/PL lines。
 * 缺失 fsCategory 映射时收集到 missingFsCodes，不静默丢弃。
 */
export function buildFsLinesFromGl(params: {
    columnDefs: GlColumnDef[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    byCode: Record<string, { fsId?: number } | undefined>;
    bsTree: any[] | undefined | null;
    plTree: any[] | undefined | null;
    /** Engagement YOA（如 2024/25）；写入 PL lines，避免 MULTI 下空 YOA 导致 Client 被切零 */
    yearOfAssessment?: string | null;
}): GlToFsMappingResult {
    const {
        columnDefs, summaryCurrentPeriod, summaryPriorYear, byCode, bsTree, plTree,
        yearOfAssessment,
    } = params;
    const plYoa = (yearOfAssessment || '').trim() || null;

    const glCurrentByFsCode = buildGlTotalByFsCode(columnDefs, summaryCurrentPeriod);
    const glPriorByFsCode = buildGlTotalByFsCode(columnDefs, summaryPriorYear);

    const missingFsCodes: MissingFsCodeEntry[] = [];
    const bsLineMap = new Map<number, FsLine>();
    const plLineMap = new Map<number, FsLine>();

    const allFsCodes = new Set([
        ...Object.keys(glCurrentByFsCode),
        ...Object.keys(glPriorByFsCode),
    ]);

    const { currentAmounts, priorAmounts } = buildResolvedGlAmountMaps(
        allFsCodes, glCurrentByFsCode, glPriorByFsCode,
        { bsTree, plTree, byCode, columnDefs },
    );

    const allResolvedFsCodes = new Set([
        ...Object.keys(currentAmounts),
        ...Object.keys(priorAmounts),
    ]);

    for (const fsCode of allResolvedFsCodes) {
        const amount = currentAmounts[fsCode] ?? 0;
        const lastYearChangeAudited = priorAmounts[fsCode] ?? 0;
        if (!isNonZero(amount) && !isNonZero(lastYearChangeAudited)) continue;

        const node = resolveGlFsNode(fsCode, byCode, bsTree, plTree);
        if (!node?.fsId) {
            missingFsCodes.push({ fsCode, currentAmount: amount, priorAmount: lastYearChangeAudited });
            continue;
        }

        const fsId = resolveTargetFsId(fsCode, node.fsId, bsTree, plTree);
        const isPl = fsCode.startsWith('PL-');
        const target = isPl ? plLineMap : bsLineMap;
        const prev = target.get(fsId);
        // 多个 GL fsCode 落到同一叶子时累加，避免后写覆盖掉本年金额
        target.set(fsId, {
            fsId,
            amount: (prev?.amount ?? 0) + amount,
            lastYearChangeAudited: (prev?.lastYearChangeAudited ?? 0) + lastYearChangeAudited,
            ...(isPl && plYoa ? { yearOfAssessment: plYoa } : {}),
        });
    }

    return {
        bsLines: Array.from(bsLineMap.values()),
        plLines: Array.from(plLineMap.values()),
        missingFsCodes,
        glCurrentByFsCode,
        glPriorByFsCode,
    };
}

const CJK_CHAR = /[\u4e00-\u9fff]/;

/**
 * 同一 fsCode 只有一个 Excel 列名时返回该列名。
 * 银行、PPE、汇率列，以及同一科目多个不同列名，不覆盖标准 FS 名。
 */
function buildUniqueGlNameByFsCode(
    columnDefs: GlColumnDef[],
    pick: (def: GlColumnDef) => string | null | undefined,
): Record<string, string> {
    const grouped = new Map<string, Set<string>>();
    for (const def of columnDefs) {
        const code = (def?.fsCode || '').trim();
        if (!code) continue;
        if (isExRateColumn(def) || isBankCashColumn(def) || isPpeColumn(def)) continue;
        const name = (pick(def) || '').trim();
        if (!name || name.toUpperCase() === code.toUpperCase()) continue;
        let names = grouped.get(code);
        if (!names) {
            names = new Set();
            grouped.set(code, names);
        }
        names.add(name);
    }
    const out: Record<string, string> = {};
    for (const [code, names] of grouped) {
        if (names.size === 1) {
            out[code] = [...names][0];
        }
    }
    return out;
}

/**
 * GL FS Summary 英文行名与 Tiny Mode 对齐。
 * preferClientAlias=false：标准名 nameEn（同 fs 多列名不同则不覆盖）。
 * preferClientAlias=true：只收集非空 aliasEn；同 fs 仅一个唯一别名时采用
 * （勿与 nameEn 混进同一 Set，否则 PL-TURN-SI 下 Sales of goods + Less: Sales Refund 会双双落空）。
 * 无别名的 fs 再回退唯一 nameEn。
 */
export function buildGlExcelNameByFsCode(
    columnDefs: GlColumnDef[],
    preferClientAlias = false,
): Record<string, string> {
    if (!preferClientAlias) {
        return buildUniqueGlNameByFsCode(columnDefs, (def) => def.nameEn);
    }
    const byAlias = buildUniqueGlNameByFsCode(columnDefs, (def) => {
        const alias = (def.aliasEn || '').trim();
        return alias || null;
    });
    const byName = buildUniqueGlNameByFsCode(columnDefs, (def) => def.nameEn);
    return { ...byName, ...byAlias };
}

/**
 * 与英文列名同一规则。
 * preferClientAlias=true：优先唯一 aliasZh；无客户中文时回退该 fs 下唯一 nameZh（保持双语）。
 */
export function buildGlExcelNameZhByFsCode(
    columnDefs: GlColumnDef[],
    preferClientAlias = false,
): Record<string, string> {
    if (!preferClientAlias) {
        return buildUniqueGlNameByFsCode(columnDefs, (def) => def.nameZh);
    }
    const byAlias = buildUniqueGlNameByFsCode(columnDefs, (def) => {
        const alias = (def.aliasZh || '').trim();
        return alias || null;
    });
    const byName = buildUniqueGlNameByFsCode(columnDefs, (def) => def.nameZh);
    return { ...byName, ...byAlias };
}

export function appendFsChineseName(english: string, chinese: string | null | undefined): string {
    const en = (english || '').trim();
    const zh = (chinese || '').trim();
    if (!zh || !CJK_CHAR.test(zh)) return en;
    if (!en || en.includes(zh)) return en || zh;
    return `${en} ${zh}`;
}

export function resolveFsChineseName(
    item: { code?: string | null; aliasZh?: string | null; nameZh?: string | null },
    glExcelNameZhByFsCode?: Record<string, string> | null,
): string | undefined {
    const fromExcel = glExcelLineName(item.code, glExcelNameZhByFsCode);
    if (fromExcel && CJK_CHAR.test(fromExcel)) return fromExcel;
    const alias = (item.aliasZh || '').trim();
    if (alias && CJK_CHAR.test(alias)) return alias;
    const name = (item.nameZh || '').trim();
    if (name && CJK_CHAR.test(name)) return name;
    return undefined;
}

export function glExcelLineName(
    code: string | null | undefined,
    glExcelNameByFsCode?: Record<string, string> | null,
): string | undefined {
    const key = (code || '').trim();
    if (!key || !glExcelNameByFsCode) return undefined;
    const name = glExcelNameByFsCode[key];
    return name && name.trim() ? name.trim() : undefined;
}

/** FS Summary Drawer：将 GL 汇总注入 fsCategoryStore 用的字符串 map */
export function buildFsDisplayValuesFromGl(params: {
    columnDefs: GlColumnDef[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    byCode: Record<string, { fsId?: number } | undefined>;
    bsTree: any[] | undefined | null;
    plTree: any[] | undefined | null;
}): GlToFsDisplayValues {
    const { byCode, bsTree, plTree } = params;
    const glCurrentByFsCode = buildGlTotalByFsCode(params.columnDefs, params.summaryCurrentPeriod);
    const glPriorByFsCode = buildGlTotalByFsCode(params.columnDefs, params.summaryPriorYear);

    const allFsCodes = new Set([
        ...Object.keys(glCurrentByFsCode),
        ...Object.keys(glPriorByFsCode),
    ]);
    const { currentAmounts, priorAmounts } = buildResolvedGlAmountMaps(
        allFsCodes, glCurrentByFsCode, glPriorByFsCode,
        { bsTree, plTree, byCode, columnDefs: params.columnDefs },
    );
    const lfpLastYear = priorAmounts['BS-EQ-LFP']
        ?? computeB61LfpLastYear(glPriorByFsCode);

    const bsValues: Record<number, string> = {};
    const plValues: Record<number, string> = {};
    const bsLastYearValues: Record<number, string> = {};
    const plLastYearValues: Record<number, string> = {};

    const allResolvedFsCodes = new Set([
        ...Object.keys(currentAmounts),
        ...Object.keys(priorAmounts),
    ]);

    for (const fsCode of allResolvedFsCodes) {
        const node = resolveGlFsNode(fsCode, byCode, bsTree, plTree);
        if (!node?.fsId) continue;
        const fsId = resolveTargetFsId(fsCode, node.fsId, bsTree, plTree);
        const amount = currentAmounts[fsCode] ?? 0;
        const prior = priorAmounts[fsCode] ?? 0;
        const add = (map: Record<number, string>, delta: number) => {
            map[fsId] = String((Number(map[fsId]) || 0) + delta);
        };
        if (fsCode.startsWith('PL-')) {
            add(plValues, amount);
            add(plLastYearValues, prior);
        } else {
            add(bsValues, amount);
            add(bsLastYearValues, prior);
        }
    }

    // 无存货时 UI 隐藏 COS；Excel 上年采购常落在 PL-COT-COS → 迁到 PUR 以免「有数但看不见」
    const cosId = byCode['PL-COT-COS']?.fsId;
    const purId = byCode['PL-COT-PUR']?.fsId;
    if (cosId && purId) {
        const cosLy = Number(plLastYearValues[cosId]) || 0;
        const purLy = Number(plLastYearValues[purId]) || 0;
        const invId = byCode['BS-CA-INV']?.fsId;
        const invLy = invId != null ? (Number(bsLastYearValues[invId]) || 0) : 0;
        const invCur = invId != null ? (Number(bsValues[invId]) || 0) : 0;
        const noInv = Math.abs(invLy) < 0.01 && Math.abs(invCur) < 0.01;
        if (noInv && Math.abs(cosLy) >= 0.01 && Math.abs(purLy) < 0.01) {
            plLastYearValues[purId] = String(cosLy);
            plLastYearValues[cosId] = '0';
        }
    }

    // B6.1 用 AL 拆分（prior AL − LFP），LFP 上年与拆分同源写入 bsLastYear + plAfterTaxLastYear
    return {
        bsValues,
        plValues,
        bsLastYearValues,
        plLastYearValues,
        lfpLastYear,
        suppressPlAfterTaxLastYear: false,
    };
}
